/**
 * Squarified treemap (Bruls, Huizing and van Wijk) for the codebase map. Sized by file count or lines, so real
 * repos lay out sensibly. Rectangles are fractions of the container (0 to 1), rendered as percentages.
 */
export interface TreemapItem<T = unknown> {
  id: string;
  /** File count or lines. Zero or negative weights are dropped. */
  weight: number;
  data?: T;
}

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface Placed<T = unknown> {
  item: TreemapItem<T>;
  rect: Rect;
}

const worst = (row: readonly number[], side: number): number => {
  const sum = row.reduce((a, b) => a + b, 0);
  const max = Math.max(...row);
  const min = Math.min(...row);
  return Math.max((side * side * max) / (sum * sum), (sum * sum) / (side * side * min));
};

/**
 * Lay items out in `bounds` (default the unit square, scaled by `aspect` = width / height so tiles come out
 * square in a wide container). Largest first; every item gets area proportional to its weight.
 */
export function squarify<T>(items: readonly TreemapItem<T>[], aspect = 1): Placed<T>[] {
  const kept = items.filter((i) => i.weight > 0).sort((a, b) => b.weight - a.weight);
  const total = kept.reduce((s, i) => s + i.weight, 0);
  if (total === 0) return [];
  // Work in a box of width `aspect` and height 1 so tile shapes are judged as drawn, then normalise.
  const area = aspect;
  const scaled = kept.map((i) => ({ item: i, a: (i.weight / total) * area }));
  const out: Placed<T>[] = [];
  let box: Rect = { x: 0, y: 0, w: aspect, h: 1 };
  let at = 0;
  while (at < scaled.length) {
    const horizontal = box.w >= box.h; // lay the row along the shorter side
    const side = Math.min(box.w, box.h);
    const row: typeof scaled = [];
    let best = Number.POSITIVE_INFINITY;
    while (at < scaled.length) {
      const next = scaled[at];
      if (!next) break;
      const trial = [...row, next].map((r) => r.a);
      const score = worst(trial, side);
      if (row.length > 0 && score > best) break;
      best = score;
      row.push(next);
      at++;
    }
    const rowArea = row.reduce((s, r) => s + r.a, 0);
    const thickness = rowArea / side;
    let offset = 0;
    for (const r of row) {
      const length = r.a / thickness;
      const rect: Rect = horizontal
        ? { x: box.x, y: box.y + offset, w: thickness, h: length }
        : { x: box.x + offset, y: box.y, w: length, h: thickness };
      out.push({ item: r.item, rect: { x: rect.x / aspect, y: rect.y, w: rect.w / aspect, h: rect.h } });
      offset += length;
    }
    box = horizontal ? { x: box.x + thickness, y: box.y, w: box.w - thickness, h: box.h } : { x: box.x, y: box.y + thickness, w: box.w, h: box.h - thickness };
  }
  return out;
}

export interface FileEntry {
  path: string;
  /** Lines, or 1 for a file count. */
  size: number;
}

/** Group files by their first `depth` path segments. `src/checkout/a.ts` at depth 2 is `src/checkout`. */
export function directoryItems(files: readonly FileEntry[], depth = 2): TreemapItem[] {
  const totals = new Map<string, number>();
  for (const f of files) {
    const parts = f.path.split("/").filter(Boolean);
    const dirs = parts.slice(0, -1);
    const key = (dirs.length === 0 ? parts.slice(0, 1) : dirs.slice(0, depth)).join("/");
    totals.set(key, (totals.get(key) ?? 0) + Math.max(1, f.size));
  }
  return [...totals].map(([id, weight]) => ({ id, weight }));
}

const pct = (n: number): string => `${Math.round(n * 10000) / 100}%`;

/** Inline position for a placed tile. */
export const rectStyle = (r: Rect): string => `left: ${pct(r.x)}; top: ${pct(r.y)}; width: ${pct(r.w)}; height: ${pct(r.h)};`;
